import React, { useState } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ImageCropper = ({ open, onClose, image, onCropComplete, onComplete, aspect = 1, circularCrop = false }) => {
  const [crop, setCrop] = useState({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5,
    aspect: aspect,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imgRef, setImgRef] = useState(null);
  const theme = useTheme();

  const onImageLoad = (img) => {
    setImgRef(img);
    // Define um crop inicial centralizado
    const width = Math.min(90, (img.width / img.height) * 90);
    const height = width;
    const y = (100 - height) / 2;
    const x = (100 - width) / 2;

    setCrop({
      unit: '%',
      width,
      height,
      x,
      y,
      aspect: aspect,
    });
  };

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // Definir um tamanho padrão para a imagem de saída (300x300 pixels)
    const outputSize = 300;
    canvas.width = outputSize;
    canvas.height = outputSize;
    
    const ctx = canvas.getContext('2d');
    
    // Aplicar um fundo branco (opcional, para imagens com transparência)
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const cropX = (crop.x * image.width) / 100;
    const cropY = (crop.y * image.height) / 100;
    const cropWidth = (crop.width * image.width) / 100;
    const cropHeight = (crop.height * image.height) / 100;
    
    // Se for crop circular, vamos criar uma máscara circular
    if (circularCrop) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    ctx.drawImage(
      image,
      cropX * scaleX,
      cropY * scaleY,
      cropWidth * scaleX,
      cropHeight * scaleY,
      0,
      0,
      outputSize,
      outputSize
    );

    if (circularCrop) {
      ctx.restore();
    }

    return new Promise((resolve) => {
      // Usar formato JPEG para garantir compatibilidade
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        
        // Certifique-se de que o blob tem um nome e tipo correto
        const file = new Blob([blob], { type: 'image/jpeg' });
        const croppedImageUrl = window.URL.createObjectURL(file);
        
        resolve({ blob: file, url: croppedImageUrl });
      }, 'image/jpeg', 0.9); // Qualidade 0.9 para boa compressão sem perda significativa
    });
  };

  const handleSave = async () => {
    if (imgRef && completedCrop?.width && completedCrop?.height) {
      try {
        const croppedImageResult = await getCroppedImg(imgRef, completedCrop);
        // Usar onComplete se estiver disponível, caso contrário usar onCropComplete
        if (onComplete) {
          onComplete(croppedImageResult);
        } else if (onCropComplete) {
          onCropComplete(croppedImageResult);
        }
        onClose();
      } catch (e) {
        console.error('Error cropping image:', e);
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 2.5,
        px: 3,
        background: theme => theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.2)' 
          : 'rgba(0, 0, 0, 0.03)'
      }}>
        <Typography 
          component="span" 
          variant="h6"
          sx={{ fontWeight: 600 }}
        >
          Ajustar Imagem
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mt: 2 
        }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 3,
              textAlign: 'center',
              maxWidth: '500px',
              borderRadius: '8px',
              py: 1.5,
              px: 3,
              backgroundColor: theme => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.03)'
            }}
          >
            Arraste para ajustar e redimensionar a área de corte. {circularCrop ? 'A imagem será cortada em formato circular.' : 'A imagem será cortada em formato quadrado.'}
          </Typography>
          {image && (
            <Box sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 1,
              backgroundColor: theme => theme.palette.mode === 'dark' 
                ? 'rgba(0, 0, 0, 0.3)' 
                : 'rgba(0, 0, 0, 0.02)'
            }}>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                aspect={aspect}
                circularCrop={circularCrop}
                minWidth={100}
                minHeight={100}
              >
                <img
                  src={image}
                  onLoad={(e) => onImageLoad(e.target)}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '500px',
                    borderRadius: '4px'
                  }}
                  alt="Imagem para corte"
                />
              </ReactCrop>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: '1px solid',
        borderColor: 'divider',
        justifyContent: 'space-between'
      }}>
        <Button 
          onClick={onClose} 
          color="inherit"
          variant="outlined"
          sx={{
            borderRadius: '10px',
            py: 1,
            px: 3,
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'text.secondary'
            }
          }}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={!completedCrop?.width || !completedCrop?.height}
          sx={{
            borderRadius: '10px',
            py: 1,
            px: 4,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            fontWeight: 600,
            '&:hover': {
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              transform: 'translateY(-2px)'
            },
            '&:disabled': {
              opacity: 0.6,
              boxShadow: 'none'
            }
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropper; 